import React, { useEffect, useState, useContext } from "react"
import {
  Typography,
  Button,
  ButtonGroup,
  Paper,
  Grid,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Box,
  Badge,
  Divider,
} from "@mui/material"
import CheckBoxTwoToneIcon from "@mui/icons-material/CheckBoxTwoTone"
import CheckBoxOutlineBlankTwoToneIcon from "@mui/icons-material/CheckBoxOutlineBlankTwoTone"
import ChatTwoToneIcon from "@mui/icons-material/ChatTwoTone"
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone"
import BookDialog from "./BookDialog"
import ContactFormDialog from "../../../components/ContactFormDialog"
import UserContext from "../../../context/UserContext"
import useFetch from "../../../hooks/useFetch"
import groupBooksByGrade from "../../../functions/groupBooksByGrade"
import BackdropContext from "../../../context/BackdropContext"
import StateContext from "../../../context/StateContext"
import { getUnreadMessages } from "../../../functions/readMessagesFunctions"
import IconLegend from "./IconLegend"

const CustomPaper = (props) => <Paper elevation={0} {...props} />

const DEFAULT_CLASS = 5

const Search = ({ isMobile, setSnackbarMsg }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  const { backdropOpen, openBackdrop, closeBackdrop } = useContext(BackdropContext)
  const { userData } = useContext(UserContext)
  const schoolID = userData.user.schoolId

  const [currentClass, setCurrentClass] = useState(DEFAULT_CLASS)
  const [currentBook, setCurrentBook] = useState(null)
  const [contactFormOpen, setContactFormOpen] = useState(false)

  const { ledgerList, ledgerLoading } = useContext(StateContext)

  const { data: bookList, loading: booksLoading } = useFetch(`/school/${schoolID}/books`, { cache: true })
  const booksByGrade = !bookList || booksLoading ? [] : groupBooksByGrade(bookList.filter((b) => !b.isBundle))
  // const bookBundlesByGrade = !bookList || booksLoading ? [] : groupBooksByGrade(bookList.filter((b) => b.isBundle))

  const loading = booksLoading || ledgerLoading

  useEffect(() => {
    // set the default grade tab to the most used one
    const usedGrades = ledgerList.map((x) => x.book.grade)

    const getMostFrequentElement = (arr = []) => {
      const entries = Object.entries(
        arr.reduce((acc, n) => {
          acc[n] = (acc[n] || 0) + 1
          return acc
        }, {})
      )

      return entries.length ? entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0] : null
    }

    const x = getMostFrequentElement(usedGrades)

    if (x) {
      setCurrentClass(Number(x))
    }
  }, [ledgerList])

  useEffect(() => {
    if (loading) {
      openBackdrop()
    } else {
      if (backdropOpen) {
        closeBackdrop()
      }
    }
  }, [loading, openBackdrop, backdropOpen, closeBackdrop])

  const getBookStatus = (row) => {
    const defaultTooltip = (
      <Tooltip arrow placement="top" title="Nicht eingestellt">
        <CheckBoxOutlineBlankTwoToneIcon />
      </Tooltip>
    )

    if (loading) {
      return defaultTooltip
    }

    const bookInLedger = ledgerList.find((e) => e.bookId === row.id)
    if (!bookInLedger) {
      return defaultTooltip
    }

    const partnerText = bookInLedger.action === "buy" ? "Verkäufer" : "Käufer"
    const actionText = bookInLedger.action === "buy" ? "Kauf" : "Verkauf"

    const statusConfig = {
      search: {
        title: `${partnerText} wird gesucht`,
        Icon: SearchTwoToneIcon,
        sx: {},
      },
      matched: {
        title: `${partnerText} gefunden`,
        Icon: ChatTwoToneIcon,
        sx: { color: "primary.main" },
      },
      finished: {
        title: `${actionText} abgeschlossen`,
        Icon: CheckBoxTwoToneIcon,
        sx: {},
      },
    }

    const config = statusConfig[bookInLedger.status]
    if (!config) {
      return defaultTooltip
    }

    const { title, Icon, sx } = config
    const badgeContent = bookInLedger.status === "matched" ? getUnreadMessages(bookInLedger) : null

    return (
      <Tooltip arrow placement="top" title={title}>
        <Badge badgeContent={badgeContent} color="secondary">
          <Icon sx={sx} />
        </Badge>
      </Tooltip>
    )
  }

  const handleClickRow = (book) => {
    setCurrentBook(book)
  }

  const handleClose = () => {
    setCurrentBook(null)
  }

  // const handleOpenBundle = () => {
  //   const currentBundle = bookList.filter((b) => b.grade === currentClass && b.isBundle)?.[0]
  //   if (!currentBundle) {
  //     return
  //   }
  //   setCurrentBook(currentBundle)
  // }

  const rows = booksByGrade[currentClass] || []

  return (
    <Box sx={{ px: 3, pb: 1, pt: isDesktop ? 12 : 1 }}>
      <Grid container justifyContent="space-between">
        <Grid item xs={12}>
          <Typography
            variant="h5"
            align={isMobile ? "center" : "left"}
            sx={{ ml: isDesktop || isMobile ? 0 : 4, color: "secondary.main", mb: 1, fontWeight: 500 }}>
            {currentClass ? `Bücherliste Klasse ${currentClass}` : "Sonstige Bücher"}
          </Typography>
          <Divider />
        </Grid>
      </Grid>

      <IconLegend isMobile={isMobile} />

      <Paper elevation={6} sx={{ borderRadius: "20px", mt: 4 }}>
        <ButtonGroup
          variant="outlined"
          sx={{ width: "100%", height: 36, borderTopLeftRadius: "20px", borderTopRightRadius: "20px", overflow: "auto" }}>
          {[5, 6, 7, 8, 9, 10, 11, 12, 13, 0].map((c) => (
            <Button
              onClick={() => setCurrentClass(c)}
              fullWidth
              key={c}
              sx={{
                backgroundColor: c !== currentClass ? "primary.main" : "white",
                color: c === currentClass ? "secondary.main" : "secondary.main",
                "&:last-child": { borderTopRightRadius: "20px" },
                "&:first-child": { borderTopLeftRadius: "20px" },
              }}>
              {c ? c : isMobile ? "☆" : "Extra"}
            </Button>
          ))}
        </ButtonGroup>

        <TableContainer
          sx={{ borderBottomRightRadius: "20px", borderBottomLeftRadius: "20px", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
          component={CustomPaper}>
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: isMobile ? "15%" : "10%" }}>
                  {/* <Tooltip arrow title="Buch gekauft oder verkauft" placement="top">
                    <CheckBoxOutlineBlankTwoToneIcon />
                  </Tooltip> */}
                  Status
                </TableCell>
                <TableCell sx={{ width: isMobile ? "75%" : "25%" }}>Titel</TableCell>
                {isMobile ? (
                  <></>
                ) : (
                  <>
                    <TableCell sx={{ width: "10%" }} align="left">
                      Fach
                    </TableCell>
                    <TableCell sx={{ width: "10%" }} align="left">
                      ISBN
                    </TableCell>
                    <TableCell sx={{ width: "10%" }} align="left">
                      Verlag
                    </TableCell>
                    <TableCell sx={{ width: "10%" }} align="left">
                      Originalpreis
                    </TableCell>
                    <TableCell sx={{ width: "20%" }} align="right">
                      Information
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  onClick={() => handleClickRow(row)}
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                  <TableCell component="th" scope="row">
                    {getBookStatus(row)}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row.title}
                  </TableCell>
                  {isMobile ? (
                    <></>
                  ) : (
                    <>
                      <TableCell align="left">{row.subject}</TableCell>
                      {/* eslint-disable-next-line */}
                      <TableCell align="left">{!!row.isbn && row.isbn != 0 ? row.isbn : ""}</TableCell>
                      <TableCell align="left">{row.publisher}</TableCell>
                      <TableCell align="left">
                        {row.priceOriginal
                          ? `${row.priceOriginal.toLocaleString("de-DE", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })} €`
                          : ""}
                      </TableCell>
                      <TableCell align="right">{row.comment}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography
        onClick={() => setContactFormOpen(true)}
        variant="caption"
        sx={{ mt: 2, mr: 2, display: "flex", justifyContent: "flex-end", cursor: "pointer" }}>
        Buch nicht gefunden?
      </Typography>

      {contactFormOpen && (
        <ContactFormDialog
          text="Du suchst ein Buch, welches hier nicht gelistet ist? Hier kannst du deine Anfrage senden:"
          placeholder="Hier fehlt noch Buch XY..."
          handleClose={() => setContactFormOpen(false)}
          onSend={() => setSnackbarMsg("Anfrage gesendet!")}
        />
      )}

      {currentBook && <BookDialog handleClose={handleClose} book={currentBook} isMobile={isMobile} />}
    </Box>
  )
}

export default Search
